import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { codeliveDropdownMenuProps } from './dropdownMenuProps';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  minSelected?: number;
  className?: string;
  disabled?: boolean;
}

const menuProps = codeliveDropdownMenuProps;

/**
 * Multi-select matching LMS admin Create Session (MUI Select + Checkbox + chips).
 */
export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = 'Select options',
  minSelected = 0,
  className = '',
  disabled = false,
}: MultiSelectDropdownProps) {
  const labelByValue = Object.fromEntries(options.map((o) => [o.value, o.label]));

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    if (next.length < minSelected) return;
    onChange(next);
  };

  const handleDelete = (value: string) => {
    if (selected.length <= minSelected) return;
    onChange(selected.filter((item) => item !== value));
  };

  return (
    <FormControl fullWidth size="small" className={className} disabled={disabled}>
      <Select
        multiple
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput />}
        displayEmpty
        MenuProps={menuProps}
        renderValue={(values) => {
          if (!values.length) {
            return <span className="text-muted">{placeholder}</span>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {values.map((value) => (
                <Chip
                  key={value}
                  label={labelByValue[value] || value}
                  size="small"
                  onDelete={(event) => {
                    event.stopPropagation();
                    handleDelete(value);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                />
              ))}
            </Box>
          );
        }}
      >
        {options.length === 0 ? (
          <MenuItem disabled value="">
            <ListItemText primary="No options available" />
          </MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selected.indexOf(option.value) > -1} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}
